import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { formatDateTime } from '../../lib/format'

type EnvState = 'set' | 'default' | 'missing' | 'unset'

interface EnvVariable {
	name: string
	group: string
	state: EnvState
	value: string | null
	secret: boolean
	purpose: string
	whenUnset: string | null
	howToObtain: string | null
}

interface EnvironmentStatus {
	generatedAt: string
	nodeEnv: string
	missing: number
	features: { label: string; ok: boolean; detail: string }[]
	variables: EnvVariable[]
}

const STATE: Record<EnvState, { label: string; className: string }> = {
	set: { label: 'Set', className: 'badge badge-live' },
	default: { label: 'Default', className: 'badge' },
	missing: { label: 'Missing', className: 'badge badge-danger' },
	unset: { label: 'Not set', className: 'badge' },
}

function VariableRow({ variable }: { variable: EnvVariable }) {
	const state = STATE[variable.state]
	const unset = variable.state === 'missing' || variable.state === 'unset'
	return (
		<tr>
			<td>
				<code className="mono" title={variable.howToObtain ?? undefined}>
					{variable.name}
				</code>
			</td>
			<td>
				<span className={state.className}>{state.label}</span>
			</td>
			<td className={variable.secret || variable.value === null ? 'muted small' : 'mono'}>{variable.value ?? '—'}</td>
			<td>
				{variable.purpose}
				{unset && variable.whenUnset && <div className="muted small">{variable.whenUnset}</div>}
				{unset && variable.howToObtain && <div className="muted small">Get one: {variable.howToObtain}</div>}
			</td>
		</tr>
	)
}

export function EnvironmentPanel() {
	const status = useQuery({ queryKey: ['admin-env'], queryFn: () => api.get<EnvironmentStatus>('/api/admin/env') })
	if (status.isPending) return <div className="page-loading">Loading…</div>
	if (status.isError) return <div className="empty">The environment is unavailable: {status.error.message}</div>
	const s = status.data
	const groups = [...new Set(s.variables.map((v) => v.group))]
	return (
		<div className="stack">
			<p className="muted small" style={{ margin: 0 }}>
				{s.nodeEnv} · read {formatDateTime(s.generatedAt)}. Variables are set in <code>.env</code> on the server and
				take effect when the app restarts. Secrets show only their length, enough to catch a truncated paste.
				{s.missing > 0 && (
					<strong className="error">
						{' '}
						{s.missing} needed variable{s.missing === 1 ? ' is' : 's are'} missing.
					</strong>
				)}
			</p>
			<div className="grid">
				{s.features.map((f) => (
					<section className="card" key={f.label}>
						<div className="row">
							<span className={f.ok ? 'badge badge-live' : 'badge badge-danger'}>{f.ok ? 'On' : 'Off'}</span>
							<strong>{f.label}</strong>
						</div>
						<p className="muted small" style={{ marginBottom: 0 }}>
							{f.detail}
						</p>
					</section>
				))}
			</div>
			{groups.map((group) => (
				<section className="card" key={group}>
					<h2>{group}</h2>
					<div className="table-scroll">
						<table>
							<thead>
								<tr>
									<th>Variable</th>
									<th>Status</th>
									<th>Value</th>
									<th>What it does</th>
								</tr>
							</thead>
							<tbody>
								{s.variables
									.filter((v) => v.group === group)
									.map((v) => (
										<VariableRow key={v.name} variable={v} />
									))}
							</tbody>
						</table>
					</div>
				</section>
			))}
		</div>
	)
}
